import type { TokenizerAndRendererExtension, Tokens } from 'marked';

/**
 * G-3 — Marked inline extension that turns `[[Cmd+K]]` style markers in
 * the moderator guide into proper `<kbd class="kbd">Cmd+K</kbd>` chips
 * so the typography matches KeyboardHelp / CommandPalette / action
 * buttons elsewhere in the workbench.
 *
 * Syntax was chosen deliberately over reusing `` `Cmd+K` `` inline-code:
 * other inline-code spans in the guide (e.g. AWS Transcribe, status
 * literals, route slugs) shouldn't be styled as keyboard chips. Double
 * square brackets are a strong, intentional opt-in.
 *
 * Multiple keys separated by `+` / `/` / space stay inside one `<kbd>`
 * — that's how the existing CSS expects it and matches the KeyboardHelp
 * rendering. If a single chip wraps two keys (e.g. `j / k`) authors can
 * write `[[j]] / [[k]]` to split them.
 */

interface KbdToken extends Tokens.Generic {
  type: 'guideKbd';
  raw: string;
  keys: string;
}

export const kbdExtension: TokenizerAndRendererExtension = {
  name: 'guideKbd',
  level: 'inline',
  start(src: string) {
    const idx = src.indexOf('[[');
    return idx < 0 ? undefined : idx;
  },
  tokenizer(src: string) {
    const m = /^\[\[([^\]\n]+?)\]\]/.exec(src);
    if (!m) return undefined;
    const token: KbdToken = {
      type: 'guideKbd',
      raw: m[0],
      keys: m[1].trim(),
    };
    return token;
  },
  renderer(token) {
    const t = token as KbdToken;
    // Escape any stray angle-brackets; the moderation-ui CSS uses
    // `.kbd, .keyboard-help__keys kbd, .action-kbd` to style chips, so
    // we emit both `.kbd` (the simple class) and a `<kbd>` element so
    // existing selectors all match.
    const safe = t.keys.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<kbd class="kbd">${safe}</kbd>`;
  },
};
